// AuthProvider.tsx
// React Auth Context for Keycloak (PKCE, public client, no client secret)
// - Forces login before rendering the app
// - Handles PKCE code flow, token storage, auto-refresh, and logout
// - Exposes useAuth() hook and <RequireAuth> gate
// - Hardened against React 18 StrictMode double-invocations

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API_BASE } from "./services/apiConfig.ts";
import { buildIdmUrl } from "./services/idmConfig.ts";
import { DEMO_DATA as BASE_DEMO_DATA } from "./data/demoCampaignData.ts";
import { ADDITIONAL_DEMO_DATA } from "./data/demoAdditionalCampaignData.ts";

const additionalDemoPaths = Object.fromEntries(
  Object.entries(ADDITIONAL_DEMO_DATA.paths).map(([campaignId, payload]: [string, any]) => [
    campaignId,
    {
      paths: {
        items: payload.paths.items.map((path: any, index: number) => ({
          ...path,
          // Keep every drive distinct while retaining only points that lie on the routed road.
          segments: path.segments.map((segment: number[][]) => {
            const trimStart = index + 1;
            const trimEnd = Math.min(index + 2, Math.max(0, segment.length - trimStart - 2));
            return segment.slice(trimStart, trimEnd > 0 ? -trimEnd : undefined);
          }),
        })),
      },
    },
  ])
);

const demoTkpByCampaign: Record<string, number> = {
  "demo-basel-zurich": 11,
  "demo-zurich-regional": 12,
};

const demoBudgetByCampaign: Record<string, number> = {
  "demo-basel-zurich": 700,
  "demo-zurich-regional": 350,
};

const additionalDemoAds = ADDITIONAL_DEMO_DATA.ads.map((ad: any) => ({
  ...ad,
  cpk: demoTkpByCampaign[ad.id] ?? ad.cpk,
  budget: demoBudgetByCampaign[ad.id] ?? ad.budget,
  budget_used: demoBudgetByCampaign[ad.id] ?? ad.budget_used,
}));

const additionalDemoMetrics = Object.fromEntries(
  Object.entries(ADDITIONAL_DEMO_DATA.metrics).map(([campaignId, payload]: [string, any]) => [
    campaignId,
    {
      ...payload,
      metrics: {
        ...payload.metrics,
        timeseries: payload.metrics.timeseries.map((day: any) => ({
          ...day,
          tkp: demoTkpByCampaign[campaignId] ?? day.tkp,
        })),
      },
    },
  ])
);

const DEMO_DATA = {
  ...BASE_DEMO_DATA,
  ads: [...BASE_DEMO_DATA.ads, ...additionalDemoAds],
  metrics: { ...BASE_DEMO_DATA.metrics, ...additionalDemoMetrics },
  heatmap: { ...BASE_DEMO_DATA.heatmap, ...ADDITIONAL_DEMO_DATA.heatmap },
  paths: { ...BASE_DEMO_DATA.paths, ...additionalDemoPaths },
  paymentInfo: { ...BASE_DEMO_DATA.paymentInfo, ...ADDITIONAL_DEMO_DATA.paymentInfo },
  metadata: { ...BASE_DEMO_DATA.metadata, ...ADDITIONAL_DEMO_DATA.metadata },
};

// ==== Configuration (adjust as needed) ====
export const TOKEN_ENDPOINT = buildIdmUrl("/realms/platform/protocol/openid-connect/token");
const AUTHORIZATION_ENDPOINT = buildIdmUrl("/realms/platform/protocol/openid-connect/auth");
export const CLIENT_ID = "api" as const;
export const ACCESS_KEY = "access_token" as const;
export const REFRESH_KEY = "refresh_token" as const;
const POST_LOGIN_PATH_KEY = "post_login_path";

const IS_DEMO_MODE =
  process.env.REACT_APP_DEMO_MODE === "true" || process.env.NODE_ENV !== "production";

// Recommended: restrict scopes to what you actually need
const SCOPE = "openid profile email";

// SPA redirect URI (MUST be whitelisted in Keycloak 'Valid Redirect URIs' and 'Web origins')
const REDIRECT_URI = window.location.origin + "/";

// Storage helpers — sessionStorage by default to avoid lingering tokens after tab closes.
const storage = sessionStorage;


// Utility: base64url encoding
const base64url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

// Utility: random string for PKCE verifier/state
const randomString = (length = 64) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ("0" + (b & 0xff).toString(16)).slice(-2)).join("");
};

// Build a SHA-256 code challenge from a verifier
const pkceChallengeFromVerifier = async (verifier: string) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64url(digest);
};

// Types for token responses
interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds
  refresh_expires_in?: number; // seconds
  token_type: string;
  scope?: string;
}

interface AuthContextShape {
  isAuthenticated: boolean;
  isAdmin: boolean;
  organization: any | null;
  organizationRole: "org_admin" | "org_member" | "admin" | null;
  isOrgAdmin: boolean;
  organizationLoading: boolean;
  isLoading: boolean;
  accessToken: string | null;
  authError: string | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>; // ensures fresh token
  fetchWithAuth: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const tokenHasAdminRole = (token: string | null) => {
  if (!token) return false;

  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return false;
    const paddedPayload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/")
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, "=");
    const payload = JSON.parse(atob(paddedPayload));
    const clientRoles = Array.isArray(payload?.resource_access?.[CLIENT_ID]?.roles)
      ? payload.resource_access[CLIENT_ID].roles
      : [];
    return clientRoles.includes("admin");
  } catch {
    return false;
  }
};

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (IS_DEMO_MODE) {
    return <DemoAuthProvider>{children}</DemoAuthProvider>;
  }

  const [accessToken, setAccessToken] = useState<string | null>(() => storage.getItem(ACCESS_KEY));
  const [refreshToken, setRefreshToken] = useState<string | null>(() => storage.getItem(REFRESH_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [organization, setOrganization] = useState<any | null>(null);
  const [organizationRole, setOrganizationRole] = useState<AuthContextShape["organizationRole"]>(null);
  const [organizationLoading, setOrganizationLoading] = useState(true);

  // store absolute expiry time in ms for proactive refresh
  const accessExpiryRef = useRef<number | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  // StrictMode guards (protect against double effect & duplicate login redirects)
  const handledCallbackRef = useRef(false);
  const loginKickoffRef = useRef(false);

  // ---- Token helpers ----
  const scheduleRefresh = useCallback((expiresInSec: number) => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // Refresh before expiry with slight jitter to avoid thundering herd
    const jitterSec = Math.floor(Math.random() * 15); // 0-14s
    const refreshInMs = Math.max(1000, (expiresInSec - 40 - jitterSec) * 1000);
    refreshTimerRef.current = window.setTimeout(async () => {
      try {
        await refreshTokens();
      } catch (e) {
        console.error("Auto-refresh failed", e);
        // Retry once after 10s without hard reload
        setTimeout(() => {
          refreshTokens().catch(err => console.error("Auto-refresh retry failed", err));
        }, 10_000);
      }
    }, refreshInMs);
  }, []); // safe: uses refreshTokens via closure at call time

  const persistTokens = useCallback(
    (t: TokenResponse) => {
      setAccessToken(t.access_token);
      setRefreshToken(t.refresh_token);
      storage.setItem(ACCESS_KEY, t.access_token);
      storage.setItem(REFRESH_KEY, t.refresh_token);

      const now = Date.now();
      const accessExpiry = now + t.expires_in * 1000;
      accessExpiryRef.current = accessExpiry;
      scheduleRefresh(t.expires_in);
      setAuthError(null);
    },
    [scheduleRefresh]
  );

  const exchangeCodeForTokens = useCallback(
    async (code: string, verifier: string, redirectUri: string) => {
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: CLIENT_ID,
        code,
        redirect_uri: redirectUri,
        code_verifier: verifier,
      });

      const res = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Token exchange failed${text ? `: ${text}` : ""}`);
      }
      const json = (await res.json()) as TokenResponse;

      persistTokens(json);

      // one-shot PKCE artifacts
      sessionStorage.removeItem("pkce_state");
      sessionStorage.removeItem("pkce_verifier");
      sessionStorage.removeItem("pkce_redirect_uri");
    },
    [persistTokens]
  );

  const refreshTokens = useCallback(async () => {
    if (!refreshToken) throw new Error("No refresh token");

    // Serialize refresh calls to avoid concurrent use of a rotating refresh token
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const run = (async () => {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
      });

      const res = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Refresh failed${text ? `: ${text}` : ""}`);
      }

      const json = (await res.json()) as TokenResponse;
      persistTokens(json);
      return json.access_token;
    })();

    refreshPromiseRef.current = run;
    try {
      return await run;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [persistTokens, refreshToken]);

  const ensureFreshToken = useCallback(async (): Promise<string | null> => {
    if (!accessToken) return null;
    // if within 25s of expiry, proactively refresh
    const now = Date.now();
    if (!accessExpiryRef.current || accessExpiryRef.current - now < 25_000) {
      try {
        return await refreshTokens();
      } catch (e) {
        console.warn("ensureFreshToken refresh failed", e);
        return null;
      }
    }
    return accessToken;
  }, [accessToken, refreshTokens]);

  const login = useCallback(async () => {
    // Reset any previous error when user explicitly retries
    setAuthError(null);

    const verifier = randomString(64);
    const challenge = await pkceChallengeFromVerifier(verifier);
    const state = randomString(24);

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath && currentPath !== "/") {
      sessionStorage.setItem(POST_LOGIN_PATH_KEY, currentPath);
    }

    sessionStorage.setItem("pkce_verifier", verifier);
    sessionStorage.setItem("pkce_state", state);
    sessionStorage.setItem("pkce_redirect_uri", REDIRECT_URI);

    const authUrl = new URL(AUTHORIZATION_ENDPOINT);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", CLIENT_ID);
    authUrl.searchParams.set("redirect_uri", REDIRECT_URI);
    authUrl.searchParams.set("scope", SCOPE);
    authUrl.searchParams.set("code_challenge_method", "S256");
    authUrl.searchParams.set("code_challenge", challenge);
    authUrl.searchParams.set("state", state);

    window.location.assign(authUrl.toString());
  }, []);

  const logout = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    accessExpiryRef.current = null;
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);

    storage.removeItem(ACCESS_KEY);
    storage.removeItem(REFRESH_KEY);

    // Optional: end-session endpoint call here if desired.

    // Force a clean reload to trigger login flow again
    window.location.replace(window.location.origin + window.location.pathname);
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!accessToken && refreshToken) {
      try {
        const t = await refreshTokens();
        return t;
      } catch {
        return null;
      }
    }
    return await ensureFreshToken();
  }, [accessToken, ensureFreshToken, refreshToken, refreshTokens]);

  const fetchWithAuth = useCallback<Required<AuthContextShape>["fetchWithAuth"]>(
    async (input, init = {}) => {
      let token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(input, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        // Try refreshing once
        token = await refreshTokens();
        const retry = await fetch(input, {
          ...init,
          headers: {
            ...(init.headers || {}),
            Authorization: `Bearer ${token}`,
          },
        });
        return retry;
      }

      return res;
    },
    [getAccessToken, refreshTokens]
  );

  useEffect(() => {
    if (!accessToken) { setOrganizationLoading(false); return; }
    // Do not auto-provision a private organization before an invited user has
    // had a chance to accept the organization deep link.
    if (window.location.pathname.startsWith("/invitations/")) {
      setOrganizationLoading(false);
      return;
    }
    fetchWithAuth(`${API_BASE}/organization`).then(async (res) => {
      if (!res.ok) throw new Error(`Organization lookup failed: ${res.status}`);
      const data = await res.json();
      setOrganization(data.organization ?? null);
      setOrganizationRole(data.role ?? null);
    }).catch((err) => console.warn(err)).finally(() => setOrganizationLoading(false));
  }, [accessToken, fetchWithAuth]);

  // Parse URL for auth code on first load (guarded against double-run)
  useEffect(() => {
    if (handledCallbackRef.current) return;
    handledCallbackRef.current = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        const storedState = sessionStorage.getItem("pkce_state");
        const verifier = sessionStorage.getItem("pkce_verifier");
        const usedRedirect = sessionStorage.getItem("pkce_redirect_uri") || REDIRECT_URI;

        if (code) {
          if (!state || !storedState || state !== storedState) {
            throw new Error("Invalid OAuth state");
          }
          if (!verifier) throw new Error("Missing PKCE verifier");

          await exchangeCodeForTokens(code, verifier, usedRedirect);

          const postLoginPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
          sessionStorage.removeItem(POST_LOGIN_PATH_KEY);

          // Restore a deep link that initiated login; otherwise clean the callback URL.
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          const cleaned = url.origin + url.pathname + url.hash; // keep SPA hash if any
          if (postLoginPath && postLoginPath.startsWith("/")) {
            window.location.replace(postLoginPath);
          } else {
            window.history.replaceState({}, "", cleaned || "/");
          }
        } else if (refreshToken) {
          await ensureFreshToken();
        }
      } catch (err) {
        console.error(err);
        setAuthError((err as Error).message || "Auth error");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(accessToken),
      isAdmin: tokenHasAdminRole(accessToken),
      organization,
      organizationRole,
      isOrgAdmin: organizationRole === "org_admin" || tokenHasAdminRole(accessToken),
      organizationLoading,
      isLoading,
      accessToken,
      authError,
      login,
      logout,
      getAccessToken,
      fetchWithAuth,
    }),
    [accessToken, fetchWithAuth, getAccessToken, isLoading, login, logout, authError, organization, organizationRole, organizationLoading]
  );

  // Gate: if unauthenticated and not loading, kick off login (guarded)
  useEffect(() => {
    if (!isLoading && !accessToken && !refreshToken && !authError) {
      if (loginKickoffRef.current) return;
      loginKickoffRef.current = true;
      // Defer to next tick to avoid any race with initial effect
      queueMicrotask(() => login());
    }
  }, [accessToken, isLoading, login, refreshToken, authError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// Component that prevents rendering children until authenticated
export const RequireAuth: React.FC<{ fallback?: React.ReactNode; children: React.ReactNode }> = ({
  fallback = null,
  children,
}) => {
  const { isAuthenticated, isLoading, authError, login } = useAuth();

  if (isLoading) return <>{fallback ?? <div className="p-6 text-center">Loading…</div>}</>;
  if (authError)
    return (
      <div className="p-6 text-center">
        Auth error: {authError}{" "}
        <button className="px-3 py-1 border rounded" onClick={login}>
          Retry
        </button>
      </div>
    );
  if (!isAuthenticated) return <>{fallback}</>;
  return <>{children}</>;
};

const createJsonResponse = (body: unknown, init?: ResponseInit) =>
  new Response(JSON.stringify(body), {
    status: init?.status ?? 200,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

const maybeHandleDemoApiRequest = async (input: RequestInfo | URL, init?: RequestInit) => {
  let urlString: string | null = null;
  if (typeof input === "string") {
    urlString = input;
  } else if (input instanceof URL) {
    urlString = input.toString();
  } else if (typeof Request !== "undefined" && input instanceof Request) {
    urlString = input.url;
  }
  if (!urlString) return null;

  let url: URL;
  try {
    url = new URL(urlString, window.location.origin);
  } catch {
    return null;
  }

  if (!url.href.startsWith(API_BASE)) return null;

  const method = (init?.method ?? (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET"))
    .toString()
    .toUpperCase();

  if (method !== "GET") {
    return createJsonResponse({ status: "demo", message: "Operation skipped in demo mode." });
  }

  const path = url.pathname.replace(/^\/+/, "");

  const data = DEMO_DATA;

  if (path === "ad") {
    return createJsonResponse({ ads: data.ads });
  }

  if (path === "ad/metrics") {
    const adId = url.searchParams.get("ad_id") ?? "";
    const payload = data.metrics[adId];
    return payload ? createJsonResponse(payload) : createJsonResponse({ error: "Not found" }, { status: 404 });
  }

  if (path === "ad/payment-info") {
    const adId = url.searchParams.get("ad_id") ?? "";
    const payload = data.paymentInfo[adId];
    return payload ? createJsonResponse(payload) : createJsonResponse({ error: "Not found" }, { status: 404 });
  }

  if (path === "ad/metadata") {
    const adId = url.searchParams.get("ad_id") ?? "";
    const payload = data.metadata[adId];
    return payload ? createJsonResponse(payload) : createJsonResponse({ metadata: null });
  }

  if (path === "ad/heatmap") {
    const adId = url.searchParams.get("ad_id") ?? "";
    const payload = data.heatmap[adId] ?? {
      ad_id: adId,
      total_scans: 0,
      matched_scans: 0,
      points: [],
      centroid: null,
      ad_start: null,
      ad_end: null,
    };
    return createJsonResponse({ heatmap: payload });
  }

  if (path === "ad/paths") {
    const adId = url.searchParams.get("ad_id") ?? "";
    const payload = data.paths[adId] ?? { paths: { items: [] } };
    return createJsonResponse(payload);
  }

  if (path === "config/value-calculation") {
    return createJsonResponse(data.valueConfig);
  }

  return createJsonResponse({ error: "Demo endpoint not implemented." }, { status: 404 });
};

const DemoAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const login = useCallback(async () => undefined, []);
  const logout = useCallback(() => undefined, []);
  const getAccessToken = useCallback(async () => null, []);
  const fetchWithAuth = useCallback<Required<AuthContextShape>["fetchWithAuth"]>(async (input, init = {}) => {
    const handled = await maybeHandleDemoApiRequest(input, init);
    if (handled) return handled;
    return fetch(input, init);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: true,
    isAdmin: false,
      organization: { id: "demo-org", name: "Demo Organization" },
      organizationRole: "org_admin",
      isOrgAdmin: true,
      organizationLoading: false,
      isLoading: false,
      accessToken: null,
      authError: null,
      login,
      logout,
      getAccessToken,
      fetchWithAuth,
    }),
    [fetchWithAuth, getAccessToken, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ===== Usage Example =====
// In your root entry (e.g., src/main.tsx or App.tsx):
//
// import { AuthProvider, RequireAuth } from "./AuthProvider";
//
// export default function App() {
//   return (
//     <AuthProvider>
//       <RequireAuth fallback={<div className="p-6">Redirecting to login…</div>}>
//         {/* your protected app here */}
//       </RequireAuth>
//     </AuthProvider>
//   );
// }
